// Google Tag Manager utilities for tracking events

interface GTMEvent {
  event: string;
  [key: string]: any;
}

// Déclarer le dataLayer global
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Fonction pour envoyer des événements à GTM
export const sendGTMEvent = (eventData: GTMEvent) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    // Vérifier le consentement avant d'envoyer des événements analytics
    const consent = localStorage.getItem('analytics_consent');
    
    // Toujours envoyer les événements de consentement et d'initialisation
    const alwaysAllowedEvents = ['consent_given', 'consent_denied', 'gtm_initialized', 'consent_update'];
    const isConsentEvent = alwaysAllowedEvents.includes(eventData.event);
    
    if (consent === 'granted' || isConsentEvent) {
      window.dataLayer.push(eventData);
      console.log('📊 GTM Event sent:', eventData);
    } else {
      console.log('🚫 GTM Event blocked (no consent):', eventData.event);
    }
  } else {
    console.warn('⚠️ GTM dataLayer not available');
  }
};

// Événements prédéfinis pour votre application
export const GTMEvents = {
  // Événements de jeu
  gameStart: (mode: 'local' | 'multiplayer', playerCount: number, difficulty: string) => 
    sendGTMEvent({
      event: 'game_start',
      game_mode: mode,
      player_count: playerCount,
      difficulty_level: difficulty,
    }),

  gameEnd: (mode: 'local' | 'multiplayer', duration: number, winner?: string) =>
    sendGTMEvent({
      event: 'game_end',
      game_mode: mode,
      game_duration: duration,
      winner: winner || 'none',
    }),

  questionAnswered: (category: string, isCorrect: boolean, difficulty: number) =>
    sendGTMEvent({
      event: 'question_answered',
      category: category,
      is_correct: isCorrect,
      difficulty: difficulty,
    }),

  categorySelected: (category: string, mode: 'local' | 'multiplayer') =>
    sendGTMEvent({
      event: 'category_selected',
      category: category,
      game_mode: mode,
    }),

  // Événements d'interface
  languageChanged: (newLanguage: string) =>
    sendGTMEvent({
      event: 'language_change',
      language: newLanguage,
    }),

  themeChanged: (newTheme: string) =>
    sendGTMEvent({
      event: 'theme_change',
      theme: newTheme,
    }),

  // Événements multijoueur
  roomCreated: (roomId: string) =>
    sendGTMEvent({
      event: 'room_created',
      room_id: roomId,
    }),

  roomJoined: (roomId: string) =>
    sendGTMEvent({
      event: 'room_joined',
      room_id: roomId,
    }),

  // Événements de navigation
  pageView: (pageName: string) =>
    sendGTMEvent({
      event: 'page_view',
      page_name: pageName,
    }),

  // Événements de consentement
  consentGiven: (consentType: string) =>
    sendGTMEvent({
      event: 'consent_given',
      consent_type: consentType,
    }),

  consentDenied: (consentType: string) =>
    sendGTMEvent({
      event: 'consent_denied',
      consent_type: consentType,
    }),
};

// Hook pour initialiser GTM dans votre app React
export const initializeGTM = () => {
  if (typeof window !== 'undefined') {
    // Initialiser dataLayer si ce n'est pas déjà fait
    window.dataLayer = window.dataLayer || [];
    
    // Vérifier le consentement existant et l'appliquer
    const savedConsent = localStorage.getItem('analytics_consent');
    const gtag = (window as any).gtag || function() {
      window.dataLayer.push(arguments);
    };
    
    if (savedConsent === 'granted') {
      gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
      console.log('✅ [GTM] Consentement existant appliqué - Analytics activés');
    } else if (savedConsent === 'denied') {
      gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
      console.log('❌ [GTM] Consentement existant appliqué - Analytics désactivés');
    } else {
      console.log('⏳ [GTM] Aucun consentement - En attente de la décision utilisateur');
    }
    
    // Envoyer un événement d'initialisation
    sendGTMEvent({
      event: 'gtm_initialized',
      timestamp: new Date().toISOString(),
      consent_status: savedConsent || 'pending',
    });
  }
};
